import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../../components/ui/dialog';
import { Badge } from '../../components/ui/badge';
import { Loader2, Plus, Pencil, Trash2 } from 'lucide-react';
import { companyAPI } from '../../lib/api';
import { toast } from 'sonner';

export default function AdminCompanies() {
  const [loading, setLoading] = useState(true);
  const [companies, setCompanies] = useState([]);
  const [showDialog, setShowDialog] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    industry: '',
    description: '',
    acquisition_date: new Date().toISOString().split('T')[0],
    status: 'active',
    employees_before: 0,
    employees_after: 0,
    revenue_before: 0,
    revenue_after: 0,
    is_public_showcase: false
  });
  
  const loadCompanies = useCallback(async () => {
    try {
      const result = await companyAPI.list();
      setCompanies(result.companies || []);
    } catch (error) {
      console.error('Load companies error:', error);
      toast.error('Failed to load companies');
    } finally {
      setLoading(false);
    }
  }, []);
  
  useEffect(() => {
    loadCompanies();
  }, [loadCompanies]);
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await companyAPI.create({
        ...formData,
        acquisition_date: new Date(formData.acquisition_date).toISOString(),
        employees_before: parseInt(formData.employees_before),
        employees_after: parseInt(formData.employees_after),
        revenue_before: parseFloat(formData.revenue_before),
        revenue_after: parseFloat(formData.revenue_after)
      });
      toast.success('Company created successfully');
      setShowDialog(false);
      setFormData({
        name: '',
        industry: '',
        description: '',
        acquisition_date: new Date().toISOString().split('T')[0],
        status: 'active',
        employees_before: 0,
        employees_after: 0,
        revenue_before: 0,
        revenue_after: 0,
        is_public_showcase: false
      });
      loadCompanies();
    } catch (error) {
      console.error('Create company error:', error);
      toast.error('Failed to create company');
    }
  };
  
  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure?')) return;
    try {
      await companyAPI.delete(id);
      toast.success('Company deleted');
      loadCompanies();
    } catch (error) {
      console.error('Delete error:', error);
      toast.error('Failed to delete company');
    }
  };
  
  return (
    <div className="min-h-screen bg-muted/30">
      <div className="border-b bg-background">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <h1 className="text-2xl font-semibold" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>Portfolio Companies</h1>
            <div className="flex gap-2">
              <Link to="/admin">
                <Button variant="outline">Back</Button>
              </Link>
              <Dialog open={showDialog} onOpenChange={setShowDialog}>
                <DialogTrigger asChild>
                  <Button data-testid="admin-portfolio-create-company-button">
                    <Plus className="mr-2 h-4 w-4" />
                    Add Company
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
                  <DialogHeader>
                    <DialogTitle>Add Portfolio Company</DialogTitle>
                  </DialogHeader>
                  <form onSubmit={handleSubmit} className="space-y-4">
                    <div>
                      <Label>Company Name *</Label>
                      <Input required value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label>Industry *</Label>
                        <Input required value={formData.industry} onChange={(e) => setFormData({ ...formData, industry: e.target.value })} />
                      </div>
                      <div>
                        <Label>Acquisition Date *</Label>
                        <Input type="date" required value={formData.acquisition_date} onChange={(e) => setFormData({ ...formData, acquisition_date: e.target.value })} />
                      </div>
                    </div>
                    <div>
                      <Label>Description *</Label>
                      <Input required value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label>Employees Before</Label>
                        <Input type="number" value={formData.employees_before} onChange={(e) => setFormData({ ...formData, employees_before: e.target.value })} />
                      </div>
                      <div>
                        <Label>Employees After</Label>
                        <Input type="number" value={formData.employees_after} onChange={(e) => setFormData({ ...formData, employees_after: e.target.value })} />
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label>Revenue Before ($)</Label>
                        <Input type="number" value={formData.revenue_before} onChange={(e) => setFormData({ ...formData, revenue_before: e.target.value })} />
                      </div>
                      <div>
                        <Label>Revenue After ($)</Label>
                        <Input type="number" value={formData.revenue_after} onChange={(e) => setFormData({ ...formData, revenue_after: e.target.value })} />
                      </div>
                    </div>
                    <div className="flex items-center space-x-2">
                      <input type="checkbox" id="public" checked={formData.is_public_showcase} onChange={(e) => setFormData({ ...formData, is_public_showcase: e.target.checked })} />
                      <Label htmlFor="public">Show on public portfolio page</Label>
                    </div>
                    <Button type="submit" className="w-full">Create Company</Button>
                  </form>
                </DialogContent>
              </Dialog>
            </div>
          </div>
        </div>
      </div>
      
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {loading ? (
          <div className="flex justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin" style={{ color: 'var(--ap-blue)' }} />
          </div>
        ) : companies.length === 0 ? (
          <Card className="rounded-xl">
            <CardContent className="py-12 text-center">
              <p className="text-muted-foreground">No companies yet. Add your first portfolio company.</p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {companies.map(company => (
              <Card key={company.id} className="rounded-xl" data-testid="admin-portfolio-company-row">
                <CardHeader>
                  <div className="flex items-start justify-between">
                    <div>
                      <CardTitle className="text-xl">{company.name}</CardTitle>
                      <div className="flex gap-2 mt-2">
                        <Badge variant="secondary">{company.industry}</Badge>
                        <Badge variant="outline">{company.status}</Badge>
                        {company.is_public_showcase && <Badge>Public</Badge>}
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <Button variant="outline" size="sm" data-testid="admin-portfolio-edit-company-button">
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <Button variant="outline" size="sm" onClick={() => handleDelete(company.id)} data-testid="admin-portfolio-delete-company-button">
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-muted-foreground mb-4">{company.description}</p>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                    <div>
                      <p className="text-muted-foreground mb-1">Employees</p>
                      <p className="font-medium">{company.employees_before} → {company.employees_after}</p>
                    </div>
                    <div>
                      <p className="text-muted-foreground mb-1">Revenue</p>
                      <p className="font-medium">${(company.revenue_before / 1000000).toFixed(1)}M → ${(company.revenue_after / 1000000).toFixed(1)}M</p>
                    </div>
                    <div>
                      <p className="text-muted-foreground mb-1">Acquired</p>
                      <p className="font-medium">{new Date(company.acquisition_date).toLocaleDateString()}</p>
                    </div>
                    <div>
                      <p className="text-muted-foreground mb-1">Growth</p>
                      <p className="font-medium text-green-600">+{Math.round(((company.revenue_after - company.revenue_before) / company.revenue_before) * 100)}%</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}